import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useCommunicationLog, useMarkChatViewed, usePatientChatLastSeen } from '../state/data/outreach';
import { useSendChatMessage } from '../state/data/chat';
import { useContentCatalog, useScheduleContentTask } from '../state/data/content';
import { useRequestAppointment } from '../state/data/appointment';
import { useFetchSuggestedReplies } from '../state/data/aiInsight';
import { visitRequestMessage } from '../logic/visits';
import { normalizeRoleId } from '../data/roles';
import useIdleAware from '../hooks/useIdleAware';
import {
  initialsOf, appSeenStates, mergeChatRows, awaitsCareTeamReply,
} from '../logic/chatMessage';
import { AvaSparkle } from './AvaAvatar';
import ChatMessageBubble, { BUBBLE_RADIUS } from './ChatMessageBubble';
import Media from './Media';
import RequestVisitModal from './RequestVisitModal';

/** Resolve the current staff role for chat sender attribution. */
function currentSenderRole() {
  return normalizeRoleId(localStorage.getItem('access_user_role')) || 'care_coordinator';
}

/* ── Constants ─────────────────────────────────────────────────── */
const POLL_INTERVAL_MS = 30000;
const MAX_MESSAGE_LENGTH = 2000;

const TRACK_COLORS = {
  onboarding: '#6366F1', 'device-mastery': '#8B5CF6', hypertension: '#EF4444',
  'diabetes-care': '#F97316', 'weight-management': '#22C55E', 'heart-health': '#EC4899',
  medication: '#3B82F6', 'sleep-health': '#A855F7', motivation: '#EAB308',
  'journey-milestones': '#14B8A6', cholesterol: '#F59E0B', custom: '#6B7280',
};

const PHASE_LABELS = { 1: 'Foundation', 2: 'Education', 3: 'Optimization', 4: 'Sustaining', 5: 'Graduating' };

const CONTENT_BASE_URL = 'https://sparks.withings.com/sparks/access-content/content/';

/* ── Content share message helpers ─────────────────────────────── */

/** Build clean, patient-readable message with a content link */
function buildContentMessage(item, personalNote) {
  const url = item.url_path?.startsWith('http') ? item.url_path : CONTENT_BASE_URL + item.slug + '.html';
  const intro = personalNote.trim()
    || `Hi! I'd like to share a resource with you that I think would be really helpful. Give it a read when you have a moment!`;
  return `${intro}\n\n📖 ${item.title}\n${url}`;
}

/* ── SharePanel — unified panel for quick replies + content sharing ─ */

function SharePanel({ patient, smartMessages: _smartMessages, onSelectQuickReply, onSelectContent, onRequestAppointment, appointmentBusy, onClose }) {
  const [showContentBrowser, setShowContentBrowser] = useState(false);
  const [search, setSearch] = useState('');
  const [trackFilter, setTrackFilter] = useState('');

  // Catalog loaded only when the content tab is opened (`enabled` gate).
  const catalogQuery = useContentCatalog(undefined, { enabled: showContentBrowser });
  // Stable ref via useMemo — the `?? []` fallback would otherwise allocate
  // a fresh array per render and bust downstream useMemo caches.
  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const loading = catalogQuery.isLoading && showContentBrowser;

  const tracks = useMemo(() => [...new Set(catalog.map(c => c.track))].sort(), [catalog]);

  const filtered = useMemo(() => {
    return catalog.filter(item => {
      if (trackFilter && item.track !== trackFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!item.title.toLowerCase().includes(q) && !item.hook?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [catalog, trackFilter, search]);

  const ps = {
    overlay: { position: 'absolute', bottom: '100%', left: 0, right: 0, zIndex: 50, marginBottom: 4 },
    container: { background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-divider)', borderRadius: 12, boxShadow: '0 -4px 20px rgba(0,0,0,0.12)', maxHeight: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--wi-divider)', flexShrink: 0 },
    filters: { display: 'flex', gap: 6, padding: '8px 14px', borderBottom: '1px solid var(--wi-divider)', flexShrink: 0 },
    list: { flex: 1, overflowY: 'auto', padding: '6px 14px' },
    item: { padding: '10px 12px', borderRadius: 8, border: '1px solid var(--wi-divider)', cursor: 'pointer', marginBottom: 6, background: 'var(--wi-background-primary)', transition: 'border-color 120ms ease' },
    quickItem: { padding: '10px 12px', borderRadius: 8, border: '1px solid var(--wi-divider)', cursor: 'pointer', marginBottom: 6, background: 'var(--wi-background-primary)', transition: 'all 120ms ease' },
    searchInput: { flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--wi-divider)', fontSize: 12, fontFamily: 'var(--wi-font-family)', outline: 'none', background: 'var(--wi-background-primary)', color: 'var(--wi-text-primary)', minWidth: 0 },
    select: { padding: '4px 6px', borderRadius: 6, border: '1px solid var(--wi-divider)', background: 'var(--wi-background-primary)', color: 'var(--wi-text-primary)', fontSize: 11, fontFamily: 'var(--wi-font-family)', outline: 'none', cursor: 'pointer' },
    trackBadge: (c) => ({ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: c + '20', color: c, whiteSpace: 'nowrap' }),
  };

  // Prepend the appointment as the first quick action — same visual as
  // any other reply, but click fires onRequestAppointment instead of inserting text.
  const appointmentItem = onRequestAppointment ? {
    id: '__appointment',
    label: '📅 Request a visit',
    text: `Choose a Care Coordinator or Nurse Practitioner visit, then send ${patient?.firstname || 'the patient'} its booking link.`,
    isAppointment: true,
  } : null;
  const contentBrowseItem = {
    id: '__browse_content',
    label: '📚 Share educational content',
    text: `Browse and share a resource from the content library with ${patient?.firstname || 'patient'}.`,
    isContentBrowse: true,
  };
  const quickActionItems = [
    ...(appointmentItem ? [appointmentItem] : []),
    contentBrowseItem,
  ];

  return (
    <div style={ps.overlay}>
      <div style={ps.container}>
        {/* Header */}
        <div style={ps.header}>
          {showContentBrowser ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setShowContentBrowser(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)', padding: '0 4px', display: 'flex', alignItems: 'center' }}
                aria-label="Back to quick actions"
              >
                &larr;
              </button>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary)' }}>Content Library</span>
            </div>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary)' }}>Quick Actions</span>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)', padding: '0 4px', flexShrink: 0 }}>&times;</button>
        </div>

        {!showContentBrowser ? (
          /* Quick actions list */
          <div style={ps.list}>
            {quickActionItems.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--wi-text-secondary)', padding: 8 }}>No quick actions available for this patient</p>
            ) : (
              quickActionItems.map(msg => (
                <div
                  key={msg.id}
                  style={{ ...ps.quickItem, opacity: msg.isAppointment && appointmentBusy ? 0.6 : 1, cursor: msg.isAppointment && appointmentBusy ? 'default' : 'pointer' }}
                  onClick={() => {
                    if (msg.isAppointment) {
                      if (!appointmentBusy) onRequestAppointment();
                    } else if (msg.isContentBrowse) {
                      setShowContentBrowser(true);
                    } else {
                      onSelectQuickReply(msg);
                      onClose();
                    }
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--wi-action-primary)'; e.currentTarget.style.background = 'var(--wi-background-info)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--wi-divider)'; e.currentTarget.style.background = 'var(--wi-background-primary)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--wi-action-primary)' }}>
                      {msg.label}{msg.isAppointment && appointmentBusy ? ' \u2014 sending\u2026' : ''}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--wi-text-secondary)', margin: 0, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {msg.text}
                  </p>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Content browser */
          <>
            <div style={ps.filters}>
              <input style={ps.searchInput} placeholder="Search content..." value={search} onChange={e => setSearch(e.target.value)} />
              <select style={ps.select} value={trackFilter} onChange={e => setTrackFilter(e.target.value)}>
                <option value="">All</option>
                {tracks.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={ps.list}>
              {loading ? (
                <p style={{ fontSize: 12, color: 'var(--wi-text-secondary)', padding: 8 }}>Loading catalog...</p>
              ) : filtered.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--wi-text-secondary)', padding: 8 }}>No content found</p>
              ) : (
                filtered.map(item => (
                  <div
                    key={item.catalog_id}
                    style={ps.item}
                    onClick={() => onSelectContent(item)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = TRACK_COLORS[item.track] || '#6B7280'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--wi-divider)'; }}
                  >
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                      <span style={ps.trackBadge(TRACK_COLORS[item.track] || '#6B7280')}>{item.track}</span>
                      <span style={{ fontSize: 10, color: 'var(--wi-text-secondary)' }}>{PHASE_LABELS[item.phase] || ''}</span>
                      {item.format && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)' }}>{item.format}</span>}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--wi-text-primary)' }}>{item.title}</div>
                    {item.hook && <div style={{ fontSize: 11, color: 'var(--wi-text-secondary)', marginTop: 2 }}>{item.hook}</div>}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Channel Toggle ────────────────────────────────────────────── */

function ChannelToggle({ channel, onChange, hasPhone }) {
  return (
    <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--wi-divider)' }}>
      <button
        onClick={() => onChange('portal')}
        style={{
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          fontFamily: 'var(--wi-font-family)',
          border: 'none',
          cursor: 'pointer',
          background: channel === 'portal' ? 'var(--wi-status-good)' : 'var(--wi-background-secondary)',
          color: channel === 'portal' ? '#fff' : 'var(--wi-text-secondary)',
          transition: 'all 150ms ease',
        }}
      >
        Portal
      </button>
      <button
        onClick={() => hasPhone ? onChange('sms') : null}
        title={hasPhone ? 'Send via SMS' : 'No phone number on file'}
        style={{
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          fontFamily: 'var(--wi-font-family)',
          border: 'none',
          borderLeft: '1px solid var(--wi-divider)',
          cursor: hasPhone ? 'pointer' : 'not-allowed',
          background: channel === 'sms' ? 'var(--wi-status-info)' : 'var(--wi-background-secondary)',
          color: channel === 'sms' ? '#fff' : hasPhone ? 'var(--wi-text-secondary)' : 'var(--wi-text-placeholder)',
          opacity: hasPhone ? 1 : 0.5,
          transition: 'all 150ms ease',
        }}
      >
        SMS
      </button>
    </div>
  );
}

/* ── Main ChatPanel Component ──────────────────────────────────── */

export default function ChatPanel({ patient, currentUser = null, onClose, embedded = false, autoFocus = false, initialMessage = '', smartMessages = [] }) {
  const pollInterval = embedded ? 6000 : POLL_INTERVAL_MS;
  const idleTimeout = embedded ? 60000 : 0;
  const isIdle = useIdleAware(idleTimeout);
  // Local optimistic adds merged on top of the server log until next refetch picks them up.
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const setMessages = (updater) => {
    setOptimisticMessages(prev => typeof updater === 'function' ? updater(prev) : updater);
  };
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState('portal');
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [pendingContent, setPendingContent] = useState(null);
  const [appointmentBanner, setAppointmentBanner] = useState(null);
  const [visitModalOpen, setVisitModalOpen] = useState(false);
  const [visitError, setVisitError] = useState(null);
  const [suggestedReplies, setSuggestedReplies] = useState([]);
  const [suggestionNote, setSuggestionNote] = useState(null);
  const sendChatMut = useSendChatMessage();
  const requestApptMut = useRequestAppointment();
  const scheduleContentMut = useScheduleContentTask();
  const fetchSuggestedMut = useFetchSuggestedReplies();
  // A ref, not isPending alone: TanStack reports isPending a tick late, and a second click inside that tick
  // would send the patient a second, irreversible message.
  const visitInFlightRef = useRef(false);
  const [visitSending, setVisitSending] = useState(false);
  const appointmentBusy = visitSending || requestApptMut.isPending;
  const [suggestLoading, setSuggestLoading] = useState(false);
  const bodyRef = useRef(null);
  const textareaRef = useRef(null);
  const latestSentAtRef = useRef(null);

  const hasPhone = Boolean(patient.phone_number);

  // SMS is unencrypted + leaves the platform — nudge staff away from PHI in the body.
  const [showSmsPhiToast, setShowSmsPhiToast] = useState(false);
  useEffect(() => {
    if (!showSmsPhiToast) return;
    const t = setTimeout(() => setShowSmsPhiToast(false), 6000);
    return () => clearTimeout(t);
  }, [showSmsPhiToast]);

  useEffect(() => {
    if (!appointmentBanner) return;
    const t = setTimeout(() => setAppointmentBanner(null), 4000);
    return () => clearTimeout(t);
  }, [appointmentBanner]);

  const selectChannel = (next) => {
    setChannel(next);
    if (next === 'sms') setShowSmsPhiToast(true);
  };

  // Polling cadence — pause when the tab is idle. `refetchInterval` as a
  // function so TanStack re-evaluates isIdle() on each tick (static value
  // is captured at render time and never updates).
  const commsQuery = useCommunicationLog(patient.userid, {
    refetchInterval: () => (isIdle() ? false : pollInterval),
  });
  const markViewedMut = useMarkChatViewed();
  // Read ticks need no 6 s cadence.
  const lastSeenQuery = usePatientChatLastSeen(patient.userid, {
    refetchInterval: () => (isIdle() ? false : POLL_INTERVAL_MS),
  });

  // Track the latest server-side sent_at as a side effect (not in render).
  useEffect(() => {
    const comms = commsQuery.data || [];
    latestSentAtRef.current = comms[0]?.sent_at || null;
  }, [commsQuery.data]);

  // Portal+SMS pairs become one bubble; optimistic entries stay until a server row matches them.
  const merged = useMemo(
    () => mergeChatRows(commsQuery.data || [], optimisticMessages),
    [commsQuery.data, optimisticMessages],
  );
  const messages = merged.rows;

  // Prune reconciled optimistic entries, or they would replay across patient navigation.
  // `pending` is a filter of the state, so equal lengths mean nothing to prune (and a re-run is a no-op).
  useEffect(() => {
    if (merged.pending.length !== optimisticMessages.length) setOptimisticMessages(merged.pending);
  }, [merged.pending, optimisticMessages.length]);
  const loading = commsQuery.isLoading;
  // A failed first load replaces the thread; a failed refetch keeps it, under the notice above the composer.
  const error = commsQuery.isLoadingError ? (commsQuery.error?.message ?? 'Unknown error') : null;
  const refetchFailed = commsQuery.isRefetchError;
  const seenStates = useMemo(
    () => appSeenStates(messages, lastSeenQuery.data, commsQuery.data || []),
    [messages, lastSeenQuery.data, commsQuery.data],
  );

  const awaitsReply = useMemo(() => awaitsCareTeamReply(messages), [messages]);

  useEffect(() => {
    if (initialMessage) setMessageText(initialMessage);
  }, [initialMessage]);

  // Mark chat as viewed once per patient mount.
  useEffect(() => {
    markViewedMut.mutate(patient.userid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.userid]);

  /* ── Sticky-bottom auto-scroll: only scroll if user was already at bottom ───── */
  // Track distance-from-bottom on every user scroll. The post-render effect
  // consumes the ref's last-known value, which reflects state *before* the
  // new messages grew scrollHeight. ~60px stickiness band.
  const wasAtBottomRef = useRef(true);
  const handleBodyScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    wasAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }, []);

  useEffect(() => {
    if (!bodyRef.current) return;
    if (!wasAtBottomRef.current) return;
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    });
  }, [messages, suggestedReplies, suggestLoading]);

  /* ── Focus textarea on initial load ────────────────────────── */
  useEffect(() => {
    if (autoFocus && !loading && textareaRef.current) {
      requestAnimationFrame(() => textareaRef.current.focus());
    }
  }, [loading, autoFocus]);

  /* ── Send message ──────────────────────────────────────────── */
  async function handleSend() {
    const text = messageText.trim();
    if (!text || sending) return;

    setSending(true);
    const now = new Date().toISOString();

    const senderFirstname = currentUser?.firstname || currentUser?.shortname || null;
    const senderLastname = currentUser?.lastname || null;

    // Optimistic UI
    const optimisticEntry = {
      communication_id: `local-${Date.now()}`,
      source: 'staff',
      channel,
      direction: 'outbound',
      message_type: channel === 'sms' ? 'staff_outreach' : 'staff_portal_message',
      body: text,
      sent_at: now,
      author_firstname: senderFirstname,
      author_lastname: senderLastname,
    };
    // Sending is an explicit "follow the conversation" action — force-scroll
    // even if the user was scrolled up reading history.
    wasAtBottomRef.current = true;
    setMessages(prev => [...prev, optimisticEntry]);
    setMessageText('');

    // Reset textarea height
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      await sendChatMut.mutateAsync({
        withingsUserId: patient.userid,
        message: text,
        options: {
          sender_role: currentSenderRole(),
          send_sms: channel === 'sms',
          sent_by: currentUser?.accountid ? String(currentUser.accountid) : undefined,
          sent_by_firstname: senderFirstname || undefined,
          sent_by_lastname: senderLastname || undefined,
        },
      });
    } catch (e) {
      console.warn('Send failed:', e.message);
    } finally {
      setSending(false);
    }
  }

  function handleContentSelect(item) {
    setPendingContent(item);
    setShowSharePanel(false);
    const intro = `Hi ${patient.firstname || 'there'}! I'd like to share a resource with you that I think could be really helpful. Give it a read when you have a moment!`;
    setMessageText(intro);
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }

  function handleQuickReply(msg) {
    setMessageText(msg.text);
    setShowSharePanel(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      requestAnimationFrame(() => {
        if (textareaRef.current) textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
      });
    }
  }

  function handleCancelContent() {
    setPendingContent(null);
  }

  /* ── Suggest replies (AI-powered) ─────────────────────────── */
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    // Only clear suggestions when new messages actually arrive, not on every poll
    if (messages.length !== prevMessageCountRef.current) {
      prevMessageCountRef.current = messages.length;
      if (messages.length > 0) setSuggestedReplies([]);
    }
  }, [messages]);

  async function handleSuggestReplies() {
    if (suggestLoading) return;
    setSuggestLoading(true);
    setSuggestedReplies([]);
    setSuggestionNote(null);
    try {
      const result = await fetchSuggestedMut.mutateAsync({
        withingsUserId: patient.userid,
        conversation: messages,
      });
      setSuggestedReplies(result.suggestions || []);
      setSuggestionNote(typeof result.note === 'string' && result.note ? result.note : null);
    } catch (e) {
      console.warn('Suggest replies failed:', e.message);
      setSuggestedReplies([]);
    } finally {
      setSuggestLoading(false);
    }
  }

  function handleSelectSuggestion(suggestion) {
    setMessageText(suggestion.text);
    setSuggestedReplies([]);
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      requestAnimationFrame(() => {
        if (textareaRef.current) textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
      });
    }
  }

  /* ── Request a visit (RequestVisitModal) ───────────────────── */
  // The event door does not return WF33's verdict, so the banner claims only that the request left.
  async function handleRequestAppointment(visitKind) {
    if (visitInFlightRef.current) return;
    visitInFlightRef.current = true;
    setVisitSending(true);
    setAppointmentBanner(null);
    setVisitError(null);
    const optimisticBody = visitRequestMessage(patient.firstname, visitKind, channel === 'sms');
    const now = new Date().toISOString();
    const apptFirstname = currentUser?.firstname || currentUser?.shortname || null;
    const apptLastname  = currentUser?.lastname || null;
    const optimisticEntry = {
      communication_id: `local-appt-${Date.now()}`,
      source: 'staff',
      channel: channel === 'sms' ? 'sms' : 'portal',
      direction: 'outbound',
      message_type: channel === 'sms' ? 'staff_outreach' : 'staff_portal_message',
      body: optimisticBody,
      sent_at: now,
      author_firstname: apptFirstname,
      author_lastname: apptLastname,
    };
    try {
      await requestApptMut.mutateAsync({
        withingsUserId: patient.userid,
        opts: {
          visitKind,
          sendSms: channel === 'sms',
          sentBy: currentUser?.accountid ? String(currentUser.accountid) : undefined,
          sentByFirstname: apptFirstname || undefined,
          sentByLastname: apptLastname || undefined,
        },
      });
      setShowSharePanel(false);
      setVisitModalOpen(false);
      // Show the optimistic chat bubble — the polled message log will reconcile.
      wasAtBottomRef.current = true;
      setMessages(prev => [...prev, optimisticEntry]);
      setAppointmentBanner({ kind: 'success', text: 'Request sent' });
    } catch (e) {
      setVisitError(e.message || 'Could not request the visit');
    } finally {
      visitInFlightRef.current = false;
      setVisitSending(false);
    }
  }

  async function handleSendWithContent() {
    if (!pendingContent || sending) return;
    const fullMessage = buildContentMessage(pendingContent, messageText);
    setSending(true);
    const now = new Date().toISOString();

    // Optimistic UI
    const optimisticEntry = {
      communication_id: `local-${Date.now()}`,
      source: 'staff',
      channel,
      direction: 'outbound',
      message_type: 'staff_portal_message',
      body: fullMessage,
      sent_at: now,
    };
    wasAtBottomRef.current = true;
    setMessages(prev => [...prev, optimisticEntry]);
    setMessageText('');
    setPendingContent(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      // Schedule the content task + send the message in parallel
      const nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + 1);
      const dateStr = nextDate.toISOString().split('T')[0];

      await Promise.all([
        sendChatMut.mutateAsync({
          withingsUserId: patient.userid,
          message: fullMessage,
          options: {
            sender_role: currentSenderRole(),
            send_sms: channel === 'sms',
          },
        }),
        scheduleContentMut.mutateAsync({
          withingsUserId: patient.userid,
          catalogId: pendingContent.catalog_id,
          scheduledDate: dateStr,
        }).catch(() => {
          // Silently skip if already scheduled (dedupe)
        }),
      ]);
    } catch (e) {
      console.warn('Send content share failed:', e.message);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (pendingContent) handleSendWithContent();
      else handleSend();
    }
  }

  function handleInput(e) {
    setMessageText(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  }

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: embedded ? 'var(--wi-background-secondary)' : 'var(--wi-background-primary)',
      borderLeft: '1px solid var(--wi-divider)',
    }}>
      {showSmsPhiToast && createPortal(
        <div className="phi-warning-toast" role="status" aria-live="polite">
          <svg className="phi-warning-toast__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 3l7 3v5c0 4.2-2.8 7.9-7 9-4.2-1.1-7-4.8-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
            <path d="M12 8.5v4M12 15.2v.05" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          <span>SMS is not encrypted — please avoid sending protected health information (PHI) in the message.</span>
          <button
            className="phi-warning-toast__close"
            onClick={() => setShowSmsPhiToast(false)}
            aria-label="Dismiss"
          >×</button>
        </div>,
        document.body,
      )}
      {/* Header */}
      <div style={{
        padding: embedded ? '10px 16px' : '16px 20px',
        borderBottom: '1px solid var(--wi-divider)',
        background: 'var(--wi-background-secondary)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
      }}>
        {/* Patient avatar — only when not embedded */}
        {!embedded && (
          <Media size={40} className="chat-avatar--patient" fallback={initialsOf(patient.firstname, patient.lastname, '?')} />
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {embedded ? (
            <p className="body2" style={{ margin: 0, fontWeight: 500 }}>Messages</p>
          ) : (
            <>
              <p className="header4" style={{ margin: 0, textTransform: 'capitalize' }}>
                {patient.firstname} {patient.lastname}
              </p>
              <p className="detail1 text-secondary" style={{ margin: '2px 0 0' }}>
                {patient.userid}
                {hasPhone && (
                  <span style={{ marginLeft: 8 }}>{patient.phone_number}</span>
                )}
              </p>
            </>
          )}
        </div>

        {/* Channel toggle — move to header when embedded */}
        {embedded && (
          <ChannelToggle channel={channel} onChange={selectChannel} hasPhone={hasPhone} />
        )}

        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              color: 'var(--wi-text-secondary)',
              fontFamily: 'var(--wi-font-family)',
              flexShrink: 0,
            }}
            aria-label="Close chat"
          >
            {'×'}
          </button>
        )}
      </div>

      {/* Message area */}
      <div
        ref={bodyRef}
        onScroll={handleBodyScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div className="loading-spinner" />
            <span className="loading-center-text">Loading messages…</span>
          </div>
        ) : error ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p className="body2" style={{ color: 'var(--wi-status-bad)' }}>
              Failed to load messages: {error}
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p className="body2 text-secondary">No messages yet. Start the conversation below.</p>
          </div>
        ) : (
          messages.map(entry => (
            <ChatMessageBubble
              key={entry.id ?? entry.communication_id ?? entry.sent_at}
              entry={entry}
              patient={patient}
              embedded={embedded}
              appSeen={seenStates.get(entry)}
            />
          ))
        )}

        {/* AI suggested replies — dashed drafts; picking one only fills the composer */}
        {suggestLoading && (
          <div role="status" className="chat-suggestion" style={{ alignSelf: 'flex-end', maxWidth: '70%', padding: '10px 16px', borderRadius: BUBBLE_RADIUS.right }}>
            <span className="detail1" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--wi-text-secondary)' }}>
              <AvaSparkle size={12} /> Generating suggestions…
            </span>
            <div className="loading-spinner" style={{ margin: '8px auto', width: 20, height: 20 }} />
          </div>
        )}
        {suggestedReplies.length > 0 && !suggestLoading && (
          <>
            <div className="detail1" style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 6, paddingTop: 6, color: 'var(--wi-text-secondary)' }}>
              <AvaSparkle size={12} />
              <span>Suggested replies · click to edit and send</span>
              <button
                type="button"
                onClick={() => setSuggestedReplies([])}
                aria-label="Dismiss suggestions"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--wi-text-secondary)', padding: '0 2px', lineHeight: 1 }}
              >&times;</button>
            </div>
            {/* For the sender only — never part of a draft. */}
            {suggestionNote && (
              <div className="detail1" style={{ alignSelf: 'flex-end', maxWidth: '80%', textAlign: 'right', color: 'var(--wi-text-secondary)' }}>
                <span style={{ fontWeight: 500 }}>For you:</span> {suggestionNote}
              </div>
            )}
            {suggestedReplies.map((s, i) => (
              <button
                type="button"
                key={s.id || i}
                onClick={() => handleSelectSuggestion(s)}
                className="body2 chat-suggestion"
                style={{
                  alignSelf: 'flex-end',
                  maxWidth: '80%',
                  minWidth: 80,
                  padding: '10px 14px',
                  borderRadius: BUBBLE_RADIUS.right,
                  fontFamily: 'inherit',
                  lineHeight: 1.5,
                  textAlign: 'left',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  cursor: 'pointer',
                }}
              >
                {s.kind === 'saved_answer' && (
                  <span className="detail1" style={{ display: 'block', marginBottom: 4, color: 'var(--wi-text-secondary)' }}>
                    Saved answer{s.title ? ` · ${s.title}` : ''}
                  </span>
                )}
                {s.text}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Outside the scroll body, which sits scrolled to the newest message. */}
      <p role="status" className="detail1 text-secondary chat-refresh-status">
        {refetchFailed && 'Could not refresh the messages. Showing the last ones loaded.'}
      </p>

      {/* Input bar */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        {/* Unified share panel */}
        {showSharePanel && (
          <SharePanel
            patient={patient}
            smartMessages={smartMessages}
            onSelectQuickReply={handleQuickReply}
            onSelectContent={handleContentSelect}
            onRequestAppointment={() => { setShowSharePanel(false); setVisitModalOpen(true); }}
            appointmentBusy={appointmentBusy}
            onClose={() => setShowSharePanel(false)}
          />
        )}
        {visitModalOpen && createPortal((
          <RequestVisitModal
            patientName={patient.firstname}
            busy={appointmentBusy}
            error={visitError}
            onConfirm={handleRequestAppointment}
            returnFocusRef={textareaRef}
            onClose={() => { setVisitModalOpen(false); setVisitError(null); }}
          />
        ), document.body)}
        {/* Appointment-request feedback banner */}
        {appointmentBanner && (
          <div style={{
            padding: '8px 16px',
            borderTop: '1px solid var(--wi-divider)',
            background: 'var(--wi-background-good)',
            color: 'var(--wi-text-primary-good)',
            fontSize: 12, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <span>{appointmentBanner.text}</span>
            <button
              onClick={() => setAppointmentBanner(null)}
              aria-label="Dismiss"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit', padding: '0 4px' }}
            >&times;</button>
          </div>
        )}

        {/* Pending content preview bar */}
        {pendingContent && (
          <div style={{
            padding: '8px 16px',
            borderTop: '1px solid var(--wi-divider)',
            background: 'var(--wi-background-info)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TRACK_COLORS[pendingContent.track] || '#6366F1'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--wi-text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Sharing: {pendingContent.title}
            </span>
            <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: (TRACK_COLORS[pendingContent.track] || '#6B7280') + '18', color: TRACK_COLORS[pendingContent.track] || '#6B7280' }}>
              {pendingContent.track}
            </span>
            <button
              onClick={handleCancelContent}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)', padding: '0 2px' }}
              aria-label="Cancel content share"
            >&times;</button>
          </div>
        )}

        <div style={{
          padding: embedded ? '10px 16px' : '12px 20px',
          borderTop: '1px solid var(--wi-divider)',
          background: 'var(--wi-background-secondary)',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
        }}>
          {!embedded && <ChannelToggle
            channel={channel}
            onChange={selectChannel}
            hasPhone={hasPhone}
          />}

          {/* Unified share button */}
          <button
            onClick={() => setShowSharePanel(prev => !prev)}
            title="Quick replies & content"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: '1px solid var(--wi-divider)',
              background: showSharePanel ? 'var(--wi-action-primary)' : 'var(--wi-background-primary)',
              color: showSharePanel ? '#fff' : 'var(--wi-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 150ms ease',
              fontSize: 20,
              fontWeight: 300,
              fontFamily: 'var(--wi-font-family)',
              lineHeight: 1,
              transform: showSharePanel ? 'rotate(45deg)' : 'none',
            }}
            aria-label="Quick replies and content"
          >
            +
          </button>

          {/* Suggest replies button — visible only when last message is from patient */}
          {awaitsReply && !pendingContent && (
            <button
              onClick={handleSuggestReplies}
              disabled={suggestLoading}
              title="Generate AI-suggested replies"
              style={{
                height: 36,
                padding: '0 12px',
                borderRadius: 8,
                border: '1px solid var(--wi-divider)',
                background: suggestLoading ? 'var(--wi-action-primary)' : 'var(--wi-background-primary)',
                color: suggestLoading ? '#fff' : 'var(--wi-action-primary)',
                cursor: suggestLoading ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'var(--wi-font-family)',
                transition: 'all 150ms ease',
              }}
              aria-label="Suggest replies"
            >
              {suggestLoading ? '…' : <><AvaSparkle size={12} /> Suggest</>}
            </button>
          )}

          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={pendingContent ? 'Add a personal note (optional)...' : channel === 'sms' ? 'Type an SMS message...' : 'Type a portal message...'}
            maxLength={MAX_MESSAGE_LENGTH}
            rows={1}
            style={{
              flex: 1,
              padding: '10px 14px',
              borderRadius: 12,
              border: pendingContent ? '1px solid var(--wi-action-primary)' : '1px solid var(--wi-divider)',
              background: 'var(--wi-background-primary)',
              fontFamily: 'var(--wi-font-family)',
              fontSize: 14,
              color: 'var(--wi-text-primary)',
              resize: 'none',
              outline: 'none',
              lineHeight: 1.4,
              minHeight: 40,
              maxHeight: 120,
            }}
          />

          <button
            onClick={pendingContent ? handleSendWithContent : handleSend}
            disabled={pendingContent ? sending : (!messageText.trim() || sending)}
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              border: 'none',
              background: (pendingContent || messageText.trim()) && !sending
                ? (channel === 'sms' ? 'var(--wi-status-info)' : 'var(--wi-status-good)')
                : 'var(--wi-action-primary-disabled)',
              color: '#fff',
              cursor: (pendingContent || messageText.trim()) && !sending ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background 150ms ease',
            }}
            aria-label="Send message"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Character count */}
      {messageText.length > MAX_MESSAGE_LENGTH * 0.8 && (
        <div style={{
          padding: '0 20px 8px',
          background: 'var(--wi-background-secondary)',
        }}>
          <span className="detail1" style={{
            color: messageText.length >= MAX_MESSAGE_LENGTH ? 'var(--wi-status-bad)' : 'var(--wi-text-placeholder)',
            fontSize: 11,
          }}>
            {messageText.length}/{MAX_MESSAGE_LENGTH}
          </span>
        </div>
      )}
    </div>
  );
}
